import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, User, Shield, Star, MapPin, Phone, Mail, Home, Save, X, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface CustomerProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  location: string;
  average_rating: number;
  total_reviews: number;
  is_suspended: boolean;
  created_at: string;
  platform_source: string;
}

export default function AdminCustomerManagement() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState<CustomerProfile | null>(null);
  const { toast } = useToast();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchCustomers();
    }
  }, [authLoading, isAdmin]);

  const fetchCustomers = async () => {
    if (!isAdmin) return;
    
    try {
      const { data: customersData, error: customersError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_type", "customer")
        .order("created_at", { ascending: false });

      if (customersError) throw customersError;

      setCustomers(customersData || []);
    } catch (error: any) {
      console.error("Error fetching customer data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch customer data. Please check your permissions.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateUKPhone = (phone: string) => {
    // UK phone number regex - accepts landline and mobile formats
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleEditCustomer = (customer: CustomerProfile) => {
    setSelectedCustomer(customer);
    setEditForm({ ...customer });
    setIsEditMode(true);
  };

  const handleSaveChanges = async () => {
    if (!editForm) return;
    
    // Validate phone number
    if (editForm.phone && editForm.phone !== "PHONE_REQUIRED" && !validateUKPhone(editForm.phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: editForm.full_name,
          email: editForm.email,
          phone: editForm.phone,
          address: editForm.address,
          location: editForm.location,
          is_suspended: editForm.is_suspended,
        })
        .eq("id", editForm.id);

      if (error) throw error;

      toast({
        title: "Customer Updated",
        description: "Customer details have been successfully updated.",
      });

      fetchCustomers();
      setIsEditMode(false);
      setSelectedCustomer(null);
      setEditForm(null);
    } catch (error: any) {
      console.error("Error updating customer:", error);
      toast({
        title: "Error",
        description: "Failed to update customer details.",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm(null);
  };

  const toggleCustomerSuspension = async (customer: CustomerProfile) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_suspended: !customer.is_suspended })
        .eq("id", customer.id);

      if (error) throw error;

      toast({
        title: "Customer Status Updated",
        description: `${customer.full_name} has been ${!customer.is_suspended ? 'suspended' : 'reactivated'}.`,
      });

      fetchCustomers();
    } catch (error: any) {
      console.error("Error updating customer status:", error);
      toast({
        title: "Error",
        description: "Failed to update customer status.",
        variant: "destructive",
      });
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading || isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-destructive" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You don't have permission to access Customer Management. Admin privileges are required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Customer Management</h2>
        <Button onClick={fetchCustomers} variant="outline">
          Refresh Data
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{customer.full_name || "Unnamed"}</div>
                      <div className="text-sm text-muted-foreground">
                        Customer ID: {customer.id.slice(0, 8)}...
                      </div>
                      <Badge variant="outline" className="text-xs mt-1">
                        {customer.platform_source === 'movable' ? 'Moving Customer' : 'Customer'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-3 h-3" />
                        {customer.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-3 h-3" />
                        {customer.phone === "PHONE_REQUIRED" ? "Not provided" : customer.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {customer.location || "Not specified"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {customer.is_suspended ? (
                      <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(customer.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditCustomer(customer)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View & Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={customer.is_suspended ? "default" : "destructive"}
                        onClick={() => toggleCustomerSuspension(customer)}
                      >
                        {customer.is_suspended ? "Reactivate" : "Suspend"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Customer Details Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => {
        setSelectedCustomer(null);
        setIsEditMode(false);
        setEditForm(null);
      }}>
        <DialogContent className="w-[95vw] max-w-2xl mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Customer Details" : "Customer Details"}</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6">
              {isEditMode && editForm ? (
                // Edit Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-name"
                        value={editForm.full_name || ""}
                        onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                        className="pl-10"
                        placeholder="Enter full name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-email"
                        type="email"
                        value={editForm.email || ""}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="pl-10"
                        placeholder="Enter email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-phone"
                        type="tel"
                        value={editForm.phone === "PHONE_REQUIRED" ? "" : editForm.phone || ""}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="pl-10"
                        placeholder="e.g., 07123456789"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-location">Location</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-location"
                        value={editForm.location || ""}
                        onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                        className="pl-10"
                        placeholder="Enter location"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="edit-address">Address</Label>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-address"
                        value={editForm.address || ""}
                        onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                        className="pl-10"
                        placeholder="Enter full address"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Full Name</h4>
                    <p className="text-lg font-medium">{selectedCustomer.full_name || "Not provided"}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Email</h4>
                    <p className="text-lg">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Phone</h4>
                    <p className="text-lg">{selectedCustomer.phone === "PHONE_REQUIRED" ? "Not provided" : selectedCustomer.phone}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Location</h4>
                    <p className="text-lg">{selectedCustomer.location || "Not specified"}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Address</h4>
                    <p className="text-lg">{selectedCustomer.address || "Not provided"}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">Account Status</h4>
                  {selectedCustomer.is_suspended ? (
                    <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">Member Since</h4>
                  <p className="text-lg">{new Date(selectedCustomer.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                {isEditMode ? (
                  <>
                    <Button onClick={handleSaveChanges} className="bg-green-600 hover:bg-green-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setIsEditMode(true)} variant="default">
                      Edit Details
                    </Button>
                    <Button
                      variant={selectedCustomer.is_suspended ? "default" : "destructive"}
                      onClick={() => {
                        toggleCustomerSuspension(selectedCustomer);
                        setSelectedCustomer(null);
                      }}
                    >
                      {selectedCustomer.is_suspended ? "Reactivate Account" : "Suspend Account"}
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
                      Close
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}